/**
 * ******************************************************************************
 * Copyright Innov'ATM all rights reserved. This software is the property of
 * Innov'ATM and may not be used in any manner except under a license agreement
 * signed with Innov'ATM.
 * *******************************************************************************
 */

import { isWithinInterval } from 'date-fns';

import { FlightTurnaroundDto, Resource, ResourceAllocation, ResourceType } from '../../../../backend';
import { ONE_HOUR } from '../../../../config/constants';
import { getTurnaroundActualOccupation } from '../RmsContent.utils';
import { ganttOneMinuteInMilliSeconds } from './gantt.constants';

export const bounded = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);
export const snapped = (value: number, step: number) => Math.round(value / step) * step;

export const resourceFields = {
    [ResourceType.TERMINAL]: { arrival: 'arrivalTerminal', departure: 'departureTerminal' },
    [ResourceType.PARKING]: { arrival: 'arrivalParking', departure: 'departureParking' },
    [ResourceType.ARR_HALL]: { arrival: 'arrivalHall', departure: null },
    [ResourceType.CHECK_IN]: { arrival: null, departure: 'counter' },
    [ResourceType.BOARDING_GATE]: { arrival: null, departure: 'departureGate' },
    [ResourceType.DEP_HALL]: { arrival: null, departure: 'departureHall' },
};

export const getResourceName = (turnaround: FlightTurnaroundDto) => turnaround?.actualOccupation?.resource || '';

export const getProposedAllocationByTurnaround = (
    proposedAllocation: ResourceAllocation[],
    turnaround: FlightTurnaroundDto,
) => {
    if (turnaround?.considerArrivalFlight && turnaround?.considerDepartureFlight) {
        return proposedAllocation?.find(
            a =>
                a.turnaround?.arr === turnaround?.arrivalFlight?.id &&
                a.turnaround?.dep === turnaround?.departureFlight?.id,
        );
    } else if (turnaround?.considerDepartureFlight) {
        return proposedAllocation?.find(a => a.turnaround?.dep && a.turnaround.dep === turnaround?.departureFlight?.id);
    } else if (turnaround?.considerArrivalFlight) {
        return proposedAllocation?.find(a => a.turnaround?.arr && a.turnaround.arr === turnaround?.arrivalFlight?.id);
    } else {
        return null;
    }
};

export const groupTurnaroundsByResourceType = (
    turnarounds: FlightTurnaroundDto[],
    resourceType: ResourceType,
    proposedAllocation: ResourceAllocation[] | null,
    resourcesByType: Resource[],
): Record<string, FlightTurnaroundDto[]> => {
    const relevantResources = resourcesByType.map(r => r.name);
    const groupedTurnarounds = turnarounds.reduce((acc, turnaround) => {
        const proposedResource = getProposedAllocationByTurnaround(proposedAllocation, turnaround)?.resource;

        //proposedResource exists or = '' otherwise turnaround's resource
        let resourceName = proposedResource?.length >= 0 ? proposedResource : getResourceName(turnaround);

        // If turnaround's allocated resource is not part of RMS resources for this resource type,
        // consider it unallocated
        if (!relevantResources.includes(resourceName)) {
            resourceName = '';
        }

        return {
            ...acc,
            [resourceName]: (acc[resourceName] || []).concat(turnaround),
        };
    }, {} as Record<string, FlightTurnaroundDto[]>);

    const sortedResources = Object.keys(groupedTurnarounds).sort((a, b) => {
        return a.localeCompare(b);
    });

    return sortedResources.reduce((acc, resource) => {
        return {
            ...acc,
            [resource]: groupedTurnarounds[resource],
        };
    }, {} as Record<string, FlightTurnaroundDto[]>);
};

export const generateGanttFlightRows = (data: { id: string; start: number; end: number }[]) => {
    const rows: Array<{ id: string; start: number; end: number }[]> = [];

    if (!data) return [];

    for (const turnaround of data) {
        let alreadyOnRows = false;
        for (const row of rows) {
            let rowContainsOverlappingFlight = false;
            for (const rowT of row) {
                if (isFlightOverlappingCurrentRowFlight(turnaround.start, rowT.start, rowT.end)) {
                    rowContainsOverlappingFlight = true;
                    break;
                }
            }
            if (!rowContainsOverlappingFlight) {
                row.push(turnaround);
                alreadyOnRows = true;
                break;
            }
        }

        if (!alreadyOnRows) {
            rows.push([turnaround]);
        }
    }
    return rows;
};
export const isFlightOverlappingCurrentRowFlight = (turnaroundStart: number, rowStart: number, rowEnd: number) => {
    if (!rowStart || !rowEnd || rowStart > rowEnd) {
        return false;
    }
    //si on veut turnarounds avec même horaire arrivalFlight / dep sur la même ligne
    //return turnaroundStart >= rowStart && turnaroundStart < rowEnd;

    return isWithinInterval(turnaroundStart, {
        start: rowStart,
        end: rowEnd,
    });
};

export const generateGanttTurnaroundRows = (turnarounds: FlightTurnaroundDto[], minTimeBetweenUse: number) => {
    const ganttFlightRows = generateGanttFlightRows(
        turnarounds
            ?.map(t => {
                const occupation = getTurnaroundActualOccupation(t);
                return {
                    id: getTurnaroundId(t) + t.considerArrivalFlight + t.considerDepartureFlight,
                    start: occupation?.occupationStart?.time,
                    end: occupation?.occupationEnd?.time + minTimeBetweenUse * ganttOneMinuteInMilliSeconds,
                };
            })
            .sort((a, b) => a.start - b.start),
    );

    if (ganttFlightRows.length === 0) {
        return [];
    } else {
        return ganttFlightRows
            ?.filter(row => row.length !== 0)
            ?.map(row =>
                row.map(o =>
                    turnarounds.find(
                        t => getTurnaroundId(t) + t.considerArrivalFlight + t.considerDepartureFlight === o.id,
                    ),
                ),
            );
    }
};

export const getNbTurnaroundsByGroup = (turnaroundsRows: FlightTurnaroundDto[][]) => {
    return turnaroundsRows.map(r => r.length).reduce((accumulator, currentValue) => accumulator + currentValue, 0);
};

export const getTurnaroundId = (turnaround: FlightTurnaroundDto) => {
    return turnaround.arrivalFlight ? turnaround.arrivalFlight.id : turnaround.departureFlight?.id;
};
export const getTurnaroundById = (
    turnaroundsOfDay: FlightTurnaroundDto[],
    id: string,
    considerArrivalFlight?: string,
    considerDepartureFlight?: string,
) => {
    const filteredTurnaround = turnaroundsOfDay.filter(
        turnaround => turnaround.arrivalFlight?.id === id || turnaround.departureFlight?.id === id,
    );
    return filteredTurnaround?.find(
        t =>
            t.considerArrivalFlight.toString() === considerArrivalFlight &&
            t.considerDepartureFlight.toString() === considerDepartureFlight,
    );
};

export const getGanttNbTimelineHours = (from: number, to: number) => {
    return Math.ceil((to - from) / ONE_HOUR);
}; //J 24h + J+1 4h
